// Copy helpers for the Astria landing page: body-text lookup, section titles,
// and the React context that hands the active language to every section.

import { createContext, useContext } from "react";
import { localizeBrand } from "../../utils/brandTerms";
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  type LandingLangCode,
} from "./landingContent";
import {
  BODY_LANG_ORDER,
  BODY_ROWS,
  HEADLINE_TRANSLATIONS,
} from "./landingTranslations";

/** Keys available in HEADLINE_TRANSLATIONS (nav labels, hero copy, section titles). */
export type HeadlineKey =
  | "nHow"
  | "nPlans"
  | "nFaq"
  | "cta"
  | "title"
  | "sub"
  | "lead"
  | "uniqueT"
  | "calmT"
  | "innerT"
  | "stepsT"
  | "profileT"
  | "privacyT"
  | "plansT"
  | "beforeT"
  | "finalT"
  | "finalCta";

const BODY_BY_ENGLISH = new Map<string, readonly string[]>(
  BODY_ROWS.map((row) => [row[0], row]),
);

/** Translates a body string from its English source; falls back to the English text. */
export const translateBody = (text: string, lang: LandingLangCode): string => {
  const column = BODY_LANG_ORDER.indexOf(lang);
  if (column <= 0) return text;
  return localizeBrand(BODY_BY_ENGLISH.get(text)?.[column] || text, lang);
};

/** Looks up a title/label for `lang`, falling back to English. */
export const getHeadline = (key: HeadlineKey, lang: LandingLangCode): string =>
  localizeBrand(
    HEADLINE_TRANSLATIONS[lang]?.[key] ||
      HEADLINE_TRANSLATIONS[DEFAULT_LANGUAGE][key] ||
      "",
    lang,
  );

export interface TextPart {
  text: string;
  emphasis: boolean;
}

/**
 * Splits `a <em>b</em> c` into parts so titles can render the italic accent
 * without dangerouslySetInnerHTML. Only <em> is recognised; anything else is text.
 */
export const parseEmphasis = (markup: string): TextPart[] => {
  const parts: TextPart[] = [];
  const pattern = /<em>(.*?)<\/em>/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(markup)) !== null) {
    if (match.index > cursor) {
      parts.push({ text: markup.slice(cursor, match.index), emphasis: false });
    }
    parts.push({ text: match[1], emphasis: true });
    cursor = match.index + match[0].length;
  }
  if (cursor < markup.length) {
    parts.push({ text: markup.slice(cursor), emphasis: false });
  }
  return parts;
};

export const isLandingLang = (
  code: string | null | undefined,
): code is LandingLangCode => LANGUAGES.some((language) => language.code === code);

/** The landing page uses regional codes where the app's i18n uses bare ones. */
const APP_LANG_BY_LANDING: Readonly<Record<string, string>> = { "zh-CN": "zh", "pt-BR": "pt" };
const LANDING_BY_APP_LANG: Readonly<Record<string, LandingLangCode>> = { zh: "zh-CN", pt: "pt-BR" };

/** App (i18n / localStorage) code for a landing language. */
export const toAppLanguage = (code: LandingLangCode): string => APP_LANG_BY_LANDING[code] ?? code;

/** Language to open the landing page in: the visitor's saved preference, else English. */
export const getInitialLanguage = (): LandingLangCode => {
  try {
    const saved = window.localStorage.getItem("preferredLanguage");
    if (isLandingLang(saved)) return saved;
    const landing = LANDING_BY_APP_LANG[saved ?? ""];
    if (landing) return landing;
  } catch {
    // Storage can be blocked (private mode); the default is fine.
  }
  return DEFAULT_LANGUAGE;
};

/** Region ids that are not ISO 3166 country codes. */
const ISO_BY_REGION: Readonly<Record<string, string>> = { UK: "GB" };

/**
 * Country name in the visitor's language. Uses the browser's own locale data,
 * so every language is covered; falls back to the English name if unavailable.
 */
export const getCountryName = (
  region: { id: string; country: string },
  lang: LandingLangCode,
): string => {
  try {
    const iso = ISO_BY_REGION[region.id] ?? region.id;
    return new Intl.DisplayNames([lang], { type: "region" }).of(iso) ?? region.country;
  } catch {
    return region.country;
  }
};

export interface LandingCopy {
  lang: LandingLangCode;
  /** Translate a body string (card text, labels, FAQ…). */
  tr: (text: string) => string;
  /** Translate a title or label. */
  headline: (key: HeadlineKey) => string;
}

export const createLandingCopy = (lang: LandingLangCode): LandingCopy => ({
  lang,
  tr: (text) => translateBody(text, lang),
  headline: (key) => getHeadline(key, lang),
});

const LandingCopyContext = createContext<LandingCopy>(
  createLandingCopy(DEFAULT_LANGUAGE),
);

export const LandingCopyProvider = LandingCopyContext.Provider;
export const useLandingCopy = (): LandingCopy => useContext(LandingCopyContext);
